import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { Helmet } from 'react-helmet-async';
// @ts-ignore
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Tab,
  Tabs,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  ContentCopy as DuplicateIcon,
  Image as ImageIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { Job, UpdateJobDto, JobType, Priority, DuplicateJobDto } from '../services/jobService';
import { Customer } from '../services/customerService';
import { Business } from '../services/businessService';
import { Appointment } from '../services/appointmentService';
import { AppointmentType } from '../services/appointmentTypeService';
import { formService, Form } from '../services/formService';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessTheme } from '../contexts/BusinessThemeContext';
import { offlineService } from '../services/offlineService';
import { PDFService } from '../services/pdfService';
import {
  documentService,
  JobDocument,
  UploadConstraints,
  FormPdfFreshness
} from '../services/documentService';
import CustomerDetailsCard from '../components/common/CustomerDetailsCard';
import JobDetailsCard from '../components/jobDetail/JobDetailsCard';
import AppointmentsList from '../components/jobDetail/AppointmentsList';
import BaseDialog from '../components/common/BaseDialog';
import JobForm from '../components/common/JobForm';
import DuplicateJobDialog from '../components/jobDetail/DuplicateJobDialog';
import DocumentGallery from '../components/jobDetail/DocumentGallery';
import FormRenderer from '../components/forms/FormRenderer';
import TabPanel from '../components/common/TabPanel';
import UnsavedChangesDialog from '../components/common/UnsavedChangesDialog';
import PixelVersionPDFViewer from '../components/jobDetail/PixelVersionPDFViewer';
import PDFViewerIframe from '../components/jobDetail/PDFViewerIframe';
import { isPixelVersionForm, generateAndStoreFormPdf } from '../services/pixelVersionPdfService';
import {
  useJob,
  useCustomer,
  useAppointmentsByJob,
  useAppointmentTypes,
  useBusinesses,
  useForms,
  useJobFormResponses,
  useUpdateJob,
  useDeleteJob,
  useDuplicateJob,
  useCommunicationLogsByJob,
  useCreateCommunicationLog,
} from '../hooks/queries';
import CommunicationLogsList from '../components/communications/CommunicationLogsList';
import CommunicationLogForm from '../components/communications/CommunicationLogForm';
import { CreateCommunicationLogDto } from '../services/communicationLogService';
import { useBusinessUsers } from '../hooks/useBusiness';
import { sortUsers } from '../utils/userHelpers';
import { extractApiErrorMessage } from '../utils/apiUtils';
import { customUserDataFieldService } from '../services/customUserDataFieldService';
import { UserDataFieldDto } from '../types/customUserDataFields';

// Memoized Lightbox wrapper — prevents parent re-renders from propagating into the
// lightbox and causing iframe repaints / PDF flashes.
const MemoizedLightbox = React.memo(Lightbox, (prevProps: any, nextProps: any) => {
  return (
    prevProps.open === nextProps.open &&
    prevProps.index === nextProps.index &&
    prevProps.slides === nextProps.slides &&
    prevProps.close === nextProps.close &&
    prevProps.carousel === nextProps.carousel &&
    prevProps.animation === nextProps.animation &&
    prevProps.render === nextProps.render &&
    prevProps.on === nextProps.on &&
    prevProps.controller === nextProps.controller &&
    prevProps.styles === nextProps.styles
  );
});

interface JobDetailPageProps {
  jobId?: string; // Optional prop for embedded usage
  embedded?: boolean; // Whether this component is embedded in another page
  customerAppointments?: Appointment[]; // Appointments passed from parent when embedded
  initialTab?: number; // Initial tab to select when embedded
  customerId?: string; // Customer ID for URL construction when embedded
  // Shared data from parent to prevent duplicate API calls
  parentAppointmentTypes?: AppointmentType[]; // Appointment types passed from parent when embedded
  parentBusiness?: Business | null; // Business data passed from parent when embedded
}

const JobDetailPage: React.FC<JobDetailPageProps> = ({ jobId, embedded = false, customerAppointments, initialTab = 0, customerId, parentAppointmentTypes, parentBusiness }) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = jobId || paramId; // Use prop if provided, otherwise use params
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useBusinessTheme();

  // Debug: track parent re-renders
  const parentRenderCountRef = useRef(0);
  parentRenderCountRef.current++;

  // TanStack Query mutations
  const updateJobMutation = useUpdateJob();
  const deleteJobMutation = useDeleteJob();
  const duplicateJobMutation = useDuplicateJob();

  // TanStack Query data fetching
  const {
    data: job,
    isLoading: jobLoading,
    isFetching: jobFetching,
    isStale: jobStale,
    error: jobError,
    refetch: refetchJob
  } = useJob(id);

  const {
    data: customerData,
    isFetching: customerFetching,
    isStale: customerStale
  } = useCustomer(job?.customerId);
  const customer = customerData ?? null;

  // For appointments: use parent data if embedded, otherwise fetch via query
  const {
    data: appointmentsFromQuery,
    isLoading: appointmentsQueryLoading,
    isFetching: appointmentsFetching,
    isStale: appointmentsStale
  } = useAppointmentsByJob(embedded ? undefined : id); // Disable query when embedded

  // Compute appointments: filter from parent if embedded, otherwise use query result
  const appointments = useMemo(() => {
    if (embedded && customerAppointments && id) {
      return customerAppointments.filter(appointment => appointment.jobId === id);
    }
    return appointmentsFromQuery || [];
  }, [embedded, customerAppointments, id, appointmentsFromQuery]);

  // For appointmentTypes: use parent data if provided, otherwise fetch
  const { data: appointmentTypesFromQuery } = useAppointmentTypes({
    businessId: user?.businessId ?? undefined,
    enabled: !parentAppointmentTypes || parentAppointmentTypes.length === 0
  });
  const appointmentTypes = parentAppointmentTypes && parentAppointmentTypes.length > 0
    ? parentAppointmentTypes
    : (appointmentTypesFromQuery || []);

  // For business: use parent data if provided, otherwise fetch
  const { data: businessesFromQuery } = useBusinesses(!parentBusiness);
  const business = parentBusiness ?? (businessesFromQuery?.[0] ?? null);

  // Business users for sales person dropdown
  const { data: businessUsersData } = useBusinessUsers({ businessId: user?.businessId ?? undefined });
  const businessUsers = useMemo(() => sortUsers(businessUsersData || []), [businessUsersData]);

  // Forms
  const { data: formsData, isLoading: formsLoading } = useForms();
  const forms = formsData || [];

  // Job form responses
  const { data: jobFormResponsesData, isLoading: formResponsesLoading, refetch: refetchFormResponses } = useJobFormResponses(id);
  const allJobFormResponses = jobFormResponsesData || [];

  // Communication logs
  const {
    data: communicationLogsData,
    isLoading: communicationLogsLoading
  } = useCommunicationLogsByJob(id);
  const createCommunicationLogMutation = useCreateCommunicationLog();

  // Local state for form responses (transformed for rendering)
  const [formResponses, setFormResponses] = useState<{ [formId: number]: any }>({});
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const loadingAppointments = appointmentsQueryLoading;
  const loadingForms = formsLoading;
  const loadingFormResponses = formResponsesLoading;
  const loading = jobLoading;
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [pendingTabChange, setPendingTabChange] = useState<number | null>(null);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Use ref to track dirty state for navigation blocking (avoids stale closure issues)
  const isFormDirtyRef = useRef(false);
  useEffect(() => {
    isFormDirtyRef.current = isFormDirty;
  }, [isFormDirty]);

  // Log when form dirty state changes
  useEffect(() => {
    console.log('[JobDetailPage] isFormDirty changed to:', isFormDirty);
  }, [isFormDirty]);

  // Warn on browser refresh/close when there are unsaved form changes
  useUnsavedChanges({ isDirty: isFormDirty });

  // Block navigation when form is dirty
  useEffect(() => {
    if (!isFormDirty) return;

    // Block browser back/forward buttons
    const handlePopState = (e: PopStateEvent) => {
      if (isFormDirtyRef.current) {
        console.log('[JobDetailPage] Blocking popstate navigation - form is dirty');
        // Push current state back to prevent navigation
        window.history.pushState(null, '', window.location.href);
        setPendingNavigation('back');
        setShowUnsavedChangesDialog(true);
      }
    };

    // Push initial state so we can detect back button
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isFormDirty]);

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [duplicateAddress, setDuplicateAddress] = useState('');
  const [tabValue, setTabValue] = useState(initialTab);
  const [isOffline, setIsOffline] = useState(!offlineService.getOnlineStatus());

  // Document state
  const [uploadedDocuments, setUploadedDocuments] = useState<JobDocument[]>([]);
  const [uploadConstraints, setUploadConstraints] = useState<UploadConstraints | null>(null);

  // Batch upload state
  const [isBatchUploading, setIsBatchUploading] = useState(false);
  const [batchSelectedFiles, setBatchSelectedFiles] = useState<any[]>([]);

  // Gallery/Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxSlides, setLightboxSlides] = useState<any[]>([]);
  const [lightboxSection, setLightboxSection] = useState<'uploaded' | 'other'>('uploaded');
  const [lightboxLoading, setLightboxLoading] = useState(false);

  // Shared thumbnail and full image URLs (shared between gallery and lightbox to prevent duplicate requests)
  const [sharedThumbnailUrls, setSharedThumbnailUrls] = useState<{ [key: string]: string | null }>({});
  const [sharedFullImageUrls, setSharedFullImageUrls] = useState<{ [key: string]: string | null }>({});

  // PDF state for PixelVersion forms
  const [pixelVersionPdfBytes, setPixelVersionPdfBytes] = useState<{ [formId: number]: Uint8Array }>({});
  const [loadingPixelVersionPdfs, setLoadingPixelVersionPdfs] = useState<{ [formId: number]: boolean }>({});

  // Form PDF freshness state for lazy PDF generation
  const [formPdfFreshness, setFormPdfFreshness] = useState<FormPdfFreshness[]>([]);
  const [checkingFreshness, setCheckingFreshness] = useState(false);
  const [regeneratingFormIds, setRegeneratingFormIds] = useState<Set<number>>(new Set());
  const regenerationQueueRef = useRef<FormPdfFreshness[]>([]);
  const isRegeneratingRef = useRef(false);

  // Prevent PDF regeneration after user operations (download/save/print)
  const skipPdfRegenerationRef = useRef(false);
  const initialPdfGenerationDoneRef = useRef(false);

  // Function to set the regeneration skip flag
  const setSkipPdfRegeneration = useCallback((skip: boolean) => {
    skipPdfRegenerationRef.current = skip;
  }, []);

  // State for salesperson's user data fields (used in forms/PDFs instead of current user's fields)
  const [salespersonUserDataFields, setSalespersonUserDataFields] = useState<UserDataFieldDto[]>([]);
  const [salespersonFieldsLoaded, setSalespersonFieldsLoaded] = useState(false);

  // Track which images are currently being loaded to prevent duplicate requests
  const loadingImagesRef = useRef<Set<string>>(new Set());

  // Update lightbox slides when full images load — only patch image src values,
  // preserving PDF (custom) slide references so PDFViewerIframe doesn't remount.
  useEffect(() => {
    if (lightboxOpen && lightboxSlides.length > 0) {
      // First, check if any image slides actually need updating before creating a new array.
      // This prevents unnecessary re-renders when only PDF (custom) slides are present
      // or when image URLs haven't changed.
      let hasChanges = false;
      for (const slide of lightboxSlides) {
        if (slide.type === 'custom') continue;
        const doc = slide.document;
        if (doc) {
          const newSrc = sharedFullImageUrls[doc.id] || sharedThumbnailUrls[doc.id] || '';
          if (newSrc !== slide.src) {
            hasChanges = true;
            break;
          }
        }
      }

      if (!hasChanges) {
        return;
      }

      console.log('[Lightbox:SlideUpdate] Changes detected, updating slides');
      setLightboxSlides(prevSlides => {
        let updatedCount = 0;
        let skippedCount = 0;
        const newSlides = prevSlides.map((slide, idx) => {
          if (slide.type === 'custom') {
            skippedCount++;
            return slide; // preserve PDF slides
          }
          const doc = slide.document;
          if (doc) {
            const newSrc = sharedFullImageUrls[doc.id] || sharedThumbnailUrls[doc.id] || '';
            if (newSrc !== slide.src) {
              updatedCount++;
              console.log(`[Lightbox:SlideUpdate] Slide ${idx} (${doc.originalFileName}) src changed:`, {
                oldSrcLength: slide.src?.length || 0,
                newSrcLength: newSrc?.length || 0,
                oldSrcPrefix: slide.src?.substring(0, 50) || '(empty)',
                newSrcPrefix: newSrc?.substring(0, 50) || '(empty)',
                fromFullImage: !!sharedFullImageUrls[doc.id],
                fromThumbnail: !!sharedThumbnailUrls[doc.id] && !sharedFullImageUrls[doc.id],
              });
              return { ...slide, src: newSrc };
            } else {
              skippedCount++;
            }
          }
          return slide;
        });
        return newSlides;
      });
    }
  }, [sharedFullImageUrls, sharedThumbnailUrls, lightboxOpen]);


  const [formData, setFormData] = useState<UpdateJobDto>({
    id: '',
    customerId: '',
    assignedSalesPersonId: user?.id || '',
    type: JobType.Consultation,
    estimatedValue: 0,
    priority: Priority.Medium
  });

  // Check URL for form slug and set initial tab when forms are loaded
  useEffect(() => {
    if (forms.length === 0 || !embedded || !customerId || !id) return;

    const pathname = window.location.pathname;
    const pathParts = pathname.split('/').filter(Boolean);

    // Expected format: crm/customers/{customerId}/jobs/{jobId}/forms/{formSlug}
    if (pathParts.length >= 7 && pathParts[0] === 'crm' && pathParts[1] === 'customers' &&
        pathParts[2] === customerId && pathParts[3] === 'jobs' && pathParts[4] === id &&
        pathParts[5] === 'forms' && pathParts[6]) {
      const formSlug = pathParts[6];
      const formIndex = forms.findIndex(form => {
        const slug = form.code || form.description.toLowerCase().replace(/\s+/g, '-');
        return slug === formSlug;
      });
      if (formIndex !== -1) {
        const newTabValue = 3 + formIndex; // Forms start at tab index 3 (after Overview, Documents, Communications)
        if (tabValue !== newTabValue) {
          setTabValue(newTabValue);
        }
      }
    }
  }, [forms, embedded, customerId, id]);

  // Debug logging for stale-while-revalidate behavior
  useEffect(() => {
    console.log('[JobDetailPage] Query state:', {
      job: {
        isLoading: jobLoading,
        isFetching: jobFetching,
        isStale: jobStale,
        hasData: !!job,
      },
      customer: {
        isFetching: customerFetching,
        isStale: customerStale,
        hasData: !!customer,
      },
      appointments: {
        isLoading: loadingAppointments,
        isFetching: appointmentsFetching,
        isStale: appointmentsStale,
        count: appointments.length,
      },
      forms: {
        isLoading: formsLoading,
        count: forms.length,
      },
      jobId: id,
    });
  }, [jobLoading, jobFetching, jobStale, job, customerFetching, customerStale, customer, loadingAppointments, appointmentsFetching, appointmentsStale, appointments, formsLoading, forms, id]);

  // Set error from query error
  useEffect(() => {
    if (jobError) {
      setError(extractApiErrorMessage(jobError, 'Failed to load job'));
    }
  }, [jobError]);

  // Offline status monitoring
  useEffect(() => {
    const handleConnectivityChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const newValue = !customEvent.detail.isOnline;
      setIsOffline(prev => prev === newValue ? prev : newValue);
    };

    // Listen for offline/online events
    window.addEventListener('connectivity-changed', handleConnectivityChange);

    // Also check offline service status periodically to catch any missed updates
    const checkOfflineStatus = () => {
      const isOnline = offlineService.getOnlineStatus();
      setIsOffline(prev => {
        const newValue = !isOnline;
        return prev === newValue ? prev : newValue;
      });
    };

    // Check immediately and then every 2 seconds
    checkOfflineStatus();
    const interval = setInterval(checkOfflineStatus, 2000);

    return () => {
      window.removeEventListener('connectivity-changed', handleConnectivityChange);
      clearInterval(interval);
    };
  }, []);

  // Fetch salesperson's user data fields when job loads
  // These fields are used in PDF generation instead of the current user's fields
  // Falls back to the current user's fields if no salesperson is assigned or fetch fails
  useEffect(() => {
    setSalespersonFieldsLoaded(false);
    if (job?.assignedSalesPersonId) {
      customUserDataFieldService.getUserFieldsByUserId(job.assignedSalesPersonId)
        .then(fields => {
          console.log('[JobDetailPage] Loaded salesperson user data fields:', fields.length);
          setSalespersonUserDataFields(fields);
          setSalespersonFieldsLoaded(true);
        })
        .catch(err => {
          console.error('Failed to load salesperson data fields:', err);
          // Fall back to current user's fields
          setSalespersonUserDataFields(user?.userDataFields || []);
          setSalespersonFieldsLoaded(true);
        });
    } else {
      // No assigned salesperson - fall back to current user's fields
      setSalespersonUserDataFields(user?.userDataFields || []);
      setSalespersonFieldsLoaded(true);
    }
  }, [job?.assignedSalesPersonId, user?.userDataFields]);

  // Reset initial PDF generation guard when navigating to a different job
  useEffect(() => {
    initialPdfGenerationDoneRef.current = false;
  }, [id]);

  // Initial PDF generation when job loads or changes (not when form data changes)
  // Note: PDFs are regenerated with fresh parameter values when switching tabs (see handleTabChange)
  // Guard: only run once per job after all data (including customer and salesperson fields) is ready.
  useEffect(() => {
    if (!job || forms.length === 0 || !customer || !salespersonFieldsLoaded || skipPdfRegenerationRef.current) return;
    if (initialPdfGenerationDoneRef.current) return;
    initialPdfGenerationDoneRef.current = true;

    // Generate PDFs only for PixelVersion forms that don't have PDFs yet
    // This runs once per job to populate the initial cache
    forms.forEach(form => {
      if (isPixelVersionForm(form) &&
          !loadingPixelVersionPdfs[form.id] &&
          !pixelVersionPdfBytes[form.id]) {
        generatePixelVersionPdf(form);
      }
    });
  }, [forms, job, customer, salespersonFieldsLoaded]);

  // Load upload constraints on mount
  useEffect(() => {
    const loadConstraints = async () => {
      try {
        const constraints = await documentService.getUploadConstraints();
        setUploadConstraints(constraints);
      } catch (err) {
        console.error('Failed to load upload constraints:', err);
      }
    };
    loadConstraints();
  }, []);

  // Load documents on mount so they're ready when user switches to Documents tab
  useEffect(() => {
    if (id && !loadingDocuments) {
      loadAllFormResponses();
    }
  }, [id]); // Only run when id is available

  // Load form responses when forms are loaded and a form tab is active
  useEffect(() => {
    if (forms.length > 0 && id && tabValue >= 3) {
      const formIndex = tabValue - 3;
      if (formIndex >= 0 && formIndex < forms.length) {
        const form = forms[formIndex];
        if (!formResponses[form.id] && !loadingFormResponses) {
          loadFormResponses(form.id);
        }
      }
    }
  }, [forms, tabValue, id]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleEditClick = async () => {
    if (!job) return;

    // Convert string enum values to numeric enum values for form
    const convertJobType = (type: JobType | string): JobType => {
      if (typeof type === 'string') {
        const stringToEnum: { [key: string]: JobType } = {
          'KitchenDesign': JobType.KitchenDesign,
          'KitchenRemodel': JobType.KitchenRemodel,
          'NewKitchen': JobType.NewKitchen,
          'Consultation': JobType.Consultation,
          'Renovation': JobType.Renovation,
          'Other': JobType.Other
        };
        return stringToEnum[type] || JobType.Consultation;
      }
      return type as JobType;
    };

    const convertPriority = (priority: Priority | string): Priority => {
      if (typeof priority === 'string') {
        const stringToEnum: { [key: string]: Priority } = {
          'Low': Priority.Low,
          'Medium': Priority.Medium,
          'High': Priority.High,
          'Urgent': Priority.Urgent
        };
        return stringToEnum[priority] || Priority.Medium;
      }
      return priority as Priority;
    };

    setFormData({
      id: job.id,
      customerId: job.customerId,
      assignedSalesPersonId: job.assignedSalesPersonId,
      jobAddress: job.jobAddress || '',
      type: convertJobType(job.type),
      estimatedValue: job.estimatedValue,
      actualValue: job.actualValue,
      priority: convertPriority(job.priority),
      expectedCompletionDate: job.expectedCompletionDate,
      notes: job.notes || ''
    });

    setIsEditDialogOpen(true);
  };

  const handleDuplicateClick = () => {
    if (!job) return;
    setDuplicateAddress(job.jobAddress || '');
    setIsDuplicateDialogOpen(true);
  };

  const handleDuplicateJob = async () => {
    if (!job) return;

    setError(null);

    const duplicateDto: DuplicateJobDto = {
      sourceJobId: job.id,
      jobAddress: duplicateAddress || undefined
    };

    duplicateJobMutation.mutate(duplicateDto, {
      onSuccess: (newJob) => {
        setIsDuplicateDialogOpen(false);
        setSuccess('Job duplicated successfully. Opening new job...');

        // Open the new job in a new tab
        const newJobUrl = `${window.location.origin}/crm/jobs/${newJob.id}`;
        window.open(newJobUrl, '_blank');
      },
      onError: (err: any) => {
        setError(extractApiErrorMessage(err, 'Failed to duplicate job'));
      },
    });
  };

  const handleUpdateJob = async () => {
    if (!job) return;

    // Clean up form data - convert empty strings to undefined for optional fields
    const cleanedFormData = {
      ...formData,
      jobAddress: formData.jobAddress || undefined,
      expectedCompletionDate: formData.expectedCompletionDate || undefined,
      notes: formData.notes || undefined
    };

    updateJobMutation.mutate(cleanedFormData, {
      onSuccess: () => {
        setIsEditDialogOpen(false);
        setSuccess('Job updated successfully');
        setError(null);
        refetchJob(); // Refresh job data via TanStack Query
      },
      onError: (err: any) => {
        setError(extractApiErrorMessage(err, 'Failed to update job'));
        setSuccess(null);
      },
    });
  };

  const handleDeleteJob = async () => {
    if (!job || !window.confirm('Are you sure you want to delete this job?')) return;

    deleteJobMutation.mutate(job.id, {
      onSuccess: () => {
        navigate('/crm/jobs');
      },
      onError: (err: any) => {
        setError(extractApiErrorMessage(err, 'Failed to delete job'));
      },
    });
  };

  const handleBack = () => {
    navigate('/crm/jobs');
  };

  // Actually perform the tab change (called directly or after dialog confirmation)
  const performTabChange = (newValue: number) => {
    console.log('[FORM_ISSUES] performTabChange called, newValue:', newValue);
    setTabValue(newValue);
    setIsFormDirty(false); // Reset dirty state when changing tabs

    // Update URL when embedded
    if (embedded && id) {
      updateUrlForTab(newValue);
    }

    // Always reload all form responses when switching to documents tab to get latest data
    if (newValue === 2 && !loadingDocuments && id) {
      console.log('[FORM_ISSUES] Switching to documents tab, calling loadAllFormResponses');
      loadAllFormResponses();
      // Also check form PDF freshness and regenerate stale PDFs
      checkFormPdfFreshnessAndRegenerate();
    }

    // Regenerate PDF when switching to a form tab to get latest parameter values
    if (newValue >= 3 && id) {
      const formIndex = newValue - 3;
      if (formIndex >= 0 && formIndex < forms.length) {
        const form = forms[formIndex];
        console.log('[FORM_ISSUES] Switching to form tab:', form.description, 'formId:', form.id, 'isPixelVersion:', isPixelVersionForm(form));

        // For PixelVersion forms, regenerate PDF to get latest parameter values
        // If we have a cached PDF, show it immediately (silent=true) and update when ready
        // If no cached PDF, show loading state (silent=false)
        if (isPixelVersionForm(form) && !loadingPixelVersionPdfs[form.id]) {
          const hasCachedPdf = !!pixelVersionPdfBytes[form.id];
          console.log('[FORM_ISSUES] PixelVersion form - calling generatePixelVersionPdf, hasCachedPdf:', hasCachedPdf);
          generatePixelVersionPdf(form, hasCachedPdf);
        }

        // Always load fresh form responses when switching tabs
        // This ensures we get the latest data including parameter values from other forms
        if (!isPixelVersionForm(form)) {
          console.log('[FORM_ISSUES] Non-PixelVersion form - calling loadFormResponses with forceRefresh=true');
          loadFormResponses(form.id, true, true);
        }
      }
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    console.log('[FORM_ISSUES] handleTabChange called, newValue:', newValue, 'isFormDirty:', isFormDirty);

    // Check if we're on a form tab and have unsaved changes
    if (isFormDirty && tabValue >= 3) {
      const formIndex = tabValue - 3;
      const currentForm = forms[formIndex];
      const isPixelVersion = currentForm && isPixelVersionForm(currentForm);

      // If it's a PixelVersion form with auto-save, allow tab change
      // Auto-save will handle saving the data (either on debounce or the hook will save when component unmounts)
      if (isPixelVersion) {
        console.log('[JobDetailPage] Form is dirty but auto-save is enabled - allowing tab change');
        // Note: The useAutoSave hook in InteractivePDFViewer will save when debounce completes
        // or immediately if we implement a forceSave on unmount
        performTabChange(newValue);
        return;
      }

      // For non-PixelVersion forms, show the unsaved changes dialog
      console.log('[JobDetailPage] Blocking tab change - form is dirty');
      setPendingTabChange(newValue);
      setShowUnsavedChangesDialog(true);
      return;
    }

    performTabChange(newValue);
  };

  // Handle dialog confirmation - proceed with pending action
  const handleConfirmLeave = () => {
    console.log('[JobDetailPage] User confirmed leave - pendingTabChange:', pendingTabChange, 'pendingNavigation:', pendingNavigation);
    setShowUnsavedChangesDialog(false);
    setIsFormDirty(false);

    if (pendingTabChange !== null) {
      performTabChange(pendingTabChange);
      setPendingTabChange(null);
    } else if (pendingNavigation === 'back') {
      // Allow back navigation
      setPendingNavigation(null);
      window.history.back();
    }
  };

  // Handle dialog cancel - stay on current page
  const handleCancelLeave = () => {
    console.log('[JobDetailPage] User cancelled leave');
    setShowUnsavedChangesDialog(false);
    setPendingTabChange(null);
    setPendingNavigation(null);
  };

  const updateUrlForTab = (tabIndex: number) => {
    if (!id || !customerId) return;

    let newPath = `/crm/customers/${customerId}/jobs/${id}`; // Base path for the job

    if (tabIndex === 1) {
      newPath = `/crm/customers/${customerId}/jobs/${id}/communications`;
    } else if (tabIndex === 2) {
      newPath = `/crm/customers/${customerId}/jobs/${id}/documents`;
    } else if (tabIndex >= 3) {
      // Form tabs - include form code in URL
      const formIndex = tabIndex - 3;
      if (formIndex >= 0 && formIndex < forms.length) {
        const form = forms[formIndex];
        // Create URL-friendly slug from form code or description
        const formSlug = form.code || form.description.toLowerCase().replace(/\s+/g, '-');
        newPath = `/crm/customers/${customerId}/jobs/${id}/forms/${formSlug}`;
      }
    }

    // Update URL without causing a navigation
    window.history.replaceState(null, '', newPath);
  };

  const loadAllFormResponses = async () => {
    if (!id) return;

    try {
      setLoadingDocuments(true);
      // Refetch form responses via TanStack Query and load documents
      const [_, documents] = await Promise.all([
        refetchFormResponses(),
        documentService.getDocumentsForJob(id)
      ]);
      setUploadedDocuments(documents);
    } catch (err: any) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Check form PDF freshness and regenerate stale PDFs
  const checkFormPdfFreshnessAndRegenerate = async () => {
    if (!id || !job) return;

    try {
      setCheckingFreshness(true);
      console.log('[FORM_ISSUES] Checking form PDF freshness...');

      const freshnessResponse = await documentService.getFormPdfFreshness(id);
      setFormPdfFreshness(freshnessResponse.forms);

      // Find stale forms that need regeneration
      const staleForms = freshnessResponse.forms.filter(f => !f.isFresh && f.hasFormResponse);

      if (staleForms.length === 0) {
        console.log('[FORM_ISSUES] All form PDFs are up to date');
        setCheckingFreshness(false);
        return;
      }

      console.log('[FORM_ISSUES] Found stale forms:', staleForms.map(f => f.formName));

      // Set up regeneration queue
      // Note: checkingFreshness will be set to false when regeneration queue is empty
      regenerationQueueRef.current = [...staleForms];
      processRegenerationQueue();
    } catch (err: any) {
      console.error('Failed to check form PDF freshness:', err);
      setCheckingFreshness(false);
    }
  };

  // Process the regeneration queue sequentially
  const processRegenerationQueue = async () => {
    if (isRegeneratingRef.current) return;
    if (regenerationQueueRef.current.length === 0) {
      // All done - refresh documents and clear checking state
      setCheckingFreshness(false);
      loadUploadedDocuments(true);
      return;
    }

    isRegeneratingRef.current = true;
    const formFreshness = regenerationQueueRef.current.shift()!;

    try {
      console.log('[FORM_ISSUES] Regenerating PDF for form:', formFreshness.formName);

      // Add to regenerating set
      setRegeneratingFormIds(prev => new Set(prev).add(formFreshness.formId));

      // Find the form definition
      const form = forms.find(f => f.id === formFreshness.formId);
      if (!form) {
        console.warn('[FORM_ISSUES] Form not found:', formFreshness.formId);
        return;
      }

      // Get form data from form responses
      const formData = await loadFormResponses(form.id, false, true) || {};

      // Get form parameter values (async call to API)
      const parameterValues = await formService.getFormFieldParameterValues(job!.id, form.id);

      // Generate and store the PDF
      await generateAndStoreFormPdf(
        form,
        job!,
        customer || null,
        appointments,
        formData,
        parameterValues,
        salespersonUserDataFields
      );

      // Update freshness status
      setFormPdfFreshness(prev =>
        prev.map(f =>
          f.formId === formFreshness.formId
            ? { ...f, isFresh: true, hasPdf: true }
            : f
        )
      );

      console.log('[FORM_ISSUES] PDF regenerated for form:', formFreshness.formName);
    } catch (err: any) {
      console.error('Failed to regenerate PDF for form:', formFreshness.formName, err);
    } finally {
      // Remove from regenerating set
      setRegeneratingFormIds(prev => {
        const next = new Set(prev);
        next.delete(formFreshness.formId);
        return next;
      });

      isRegeneratingRef.current = false;

      // Process next in queue
      processRegenerationQueue();
    }
  };

  const loadUploadedDocuments = async (forceRefresh = false) => {
    if (!id) return;

    try {
      const documents = await documentService.getDocumentsForJob(id, forceRefresh);
      setUploadedDocuments(documents);
    } catch (err: any) {
      console.error('Failed to load uploaded documents:', err);
    }
  };

  const loadFormResponses = async (formId: number, updateState: boolean = true, forceRefresh: boolean = false): Promise<{ [key: string]: any } | undefined> => {
    console.log('[FORM_ISSUES] loadFormResponses called:', { formId, updateState, forceRefresh });
    if (!id) {
      console.log('[FORM_ISSUES] loadFormResponses - no id, returning undefined');
      return undefined;
    }

    try {
      // Use TanStack Query data if available, otherwise refetch
      // Always refetch if forceRefresh is true (e.g., after form submission or tab switch)
      let responses = allJobFormResponses;
      const needsRefetch = !responses || responses.length === 0 || forceRefresh;
      console.log('[FORM_ISSUES] loadFormResponses - current allJobFormResponses count:', responses?.length || 0, 'needsRefetch:', needsRefetch);

      if (needsRefetch) {
        // When force refreshing AND updating state, clear all local formResponses
        // so they get re-transformed from fresh data when switching tabs
        if (forceRefresh && updateState) {
          console.log('[FORM_ISSUES] loadFormResponses - clearing all formResponses state');
          setFormResponses({});
        }
        console.log('[FORM_ISSUES] loadFormResponses - calling refetchFormResponses()');
        const result = await refetchFormResponses();
        responses = result.data || [];
        console.log('[FORM_ISSUES] loadFormResponses - refetch complete, new responses count:', responses.length);
      }

      // Filter responses for this form and transform to form data
      const form = forms.find(f => f.id === formId);
      if (form) {
        // NEW FORMAT: Filter by formId (one response per form)
        const formResponsesForForm = responses.filter(response =>
          response.formId === formId
        );
        console.log('[FORM_ISSUES] loadFormResponses - found', formResponsesForForm.length, 'responses for formId:', formId);

        // Transform to form data format
        const formData = formService.transformJobFormResponsesToFormData(
          formResponsesForForm,
          form
        );
        console.log('[FORM_ISSUES] loadFormResponses - transformed formData keys:', Object.keys(formData));

        if (updateState) {
          console.log('[FORM_ISSUES] loadFormResponses - updating formResponses state for formId:', formId);
          setFormResponses(prev => ({
            ...prev,
            [formId]: formData
          }));
        }

        return formData;
      } else {
        console.log('[FORM_ISSUES] loadFormResponses - form not found for formId:', formId);
      }
    } catch (err: any) {
      console.error('[FORM_ISSUES] Failed to load form responses:', err);
      // Don't show error to user, just log it
    }

    return undefined;
  };


  // Generate document filename using the same format as FormRenderer
  const generateDocumentFilename = (form: Form): string => {
    const sanitizedFormName = form.description.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    const sanitizedJobNumber = job?.jobNumber ? job.jobNumber.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') : 'job';
    return `${sanitizedJobNumber}-${sanitizedFormName}.pdf`;
  };

  // Get forms that have been filled out (have responses) and are printable
  const getFormsWithResponses = (): Form[] => {
    if (allJobFormResponses.length === 0) return [];

    // NEW FORMAT: Check if form has a response by formId
    // Find forms that have at least one response and are printable
    const formsWithResponses: Form[] = [];
    forms.forEach(form => {
      // Only include forms that are printable
      if (!form.isPrintable) return;

      const hasResponse = allJobFormResponses.some(response =>
        response.formId === form.id && response.isActive
      );
      
      if (hasResponse) {
        formsWithResponses.push(form);
      }
    });

    // Sort by DisplayOrder
    return formsWithResponses.sort((a, b) => a.displayOrder - b.displayOrder);
  };

  // Helper function to check if form has PixelVersion enabled
  const isPixelVersionForm = (form: Form): boolean => {
    if (!form.pdfConfigurationJson) return false;

    try {
      const pdfConfig = JSON.parse(form.pdfConfigurationJson);
      return pdfConfig.pixelVersion === true;
    } catch (error) {
      console.warn('Failed to parse PDF configuration JSON:', error);
      return false;
    }
  };

  // Generate PDF for PixelVersion forms
  // overrideFormData: optional form data to use instead of fetching from API (used when applying parameter changes)
  const generatePixelVersionPdf = async (form: Form, silent: boolean = false, overrideFormData?: { [key: string]: any }) => {
    console.log('[FORM_ISSUES] generatePixelVersionPdf called for form:', form.description, 'formId:', form.id, 'silent:', silent, 'hasOverrideData:', !!overrideFormData);
    if (!id || !job) {
      console.log('[FORM_ISSUES] generatePixelVersionPdf - no id or job, returning');
      return;
    }

    try {
      // Only show loading state if not silent (when we have a cached PDF to show)
      if (!silent) {
        setLoadingPixelVersionPdfs(prev => ({ ...prev, [form.id]: true }));
      }
      setError(null);

      let mergedFormData: { [key: string]: any };
      let formParameterValues: { [fieldId: number]: string } = {};

      if (overrideFormData) {
        // Use provided form data directly (e.g., when applying parameter changes)
        // Still fetch parameter values for PDF generation options
        console.log('[FORM_ISSUES] generatePixelVersionPdf - using override form data');
        formParameterValues = await formService.getFormFieldParameterValues(id, form.id);
        mergedFormData = overrideFormData;
      } else {
        // Fetch parameter values and form responses in parallel
        // Parameter values are fetched fresh from API each time we generate
        // Force refresh form responses to get latest data from other forms
        // Use updateState=false to avoid an intermediate render; we batch the state update below
        console.log('[FORM_ISSUES] generatePixelVersionPdf - fetching form data and parameter values');
        const [formData, fetchedParameterValues] = await Promise.all([
          loadFormResponses(form.id, false, true),
          formService.getFormFieldParameterValues(id, form.id)
        ]);
        formParameterValues = fetchedParameterValues;
        // Merge parameter values (preloaded from other forms) with saved form data
        // Parameter values act as defaults, saved form data takes precedence
        mergedFormData = { ...(formParameterValues || {}), ...(formData || {}) };
        console.log('[FORM_ISSUES] generatePixelVersionPdf - formData keys:', Object.keys(formData || {}), 'parameterValues keys:', Object.keys(formParameterValues || {}), 'mergedFormData keys:', Object.keys(mergedFormData));
      }

      // Diagnostic: log UserDataField values being passed to PDF generation
      console.log('[UDF_DEBUG] generatePixelVersionPdf - salespersonUserDataFields count:', salespersonUserDataFields?.length,
        'fields:', salespersonUserDataFields?.map(f => ({ code: f.businessDataField?.code, value: f.value })));
      console.log('[UDF_DEBUG] generatePixelVersionPdf - mergedFormData sample (first 10 keys):',
        Object.entries(mergedFormData).slice(0, 10).map(([k, v]) => `${k}=${v}`));

      const initialFormData = mergedFormData;

      // Get form sections
      const formSections = form.formSections || [];

      // Generate filename
      const sanitizedFormName = form.description.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
      const sanitizedJobNumber = job.jobNumber ? job.jobNumber.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') : 'job';
      const filename = `${sanitizedJobNumber}-${sanitizedFormName}.pdf`;

      // Parse PDF configuration from form
      let pdfConfig: any = {};
      if (form.pdfConfigurationJson) {
        try {
          pdfConfig = JSON.parse(form.pdfConfigurationJson);
        } catch (error) {
          console.warn('Failed to parse PDF configuration JSON:', error);
        }
      }

      // Prepare header data for placeholder replacement (e.g., {{ DisplayName }})
      const headerData: { [key: string]: string | number | boolean | null | undefined } = {
        DisplayName: job.assignedSalesPersonName || ''
      };

      // Extract first appointment from job for parameter injection
      let firstAppointment = null;
      if (appointments?.length > 0) {
        const sortedAppointments = [...appointments].sort((a: any, b: any) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
        const firstAppt = sortedAppointments[0];
        console.log('[JobDetailPage] First appointment found:', firstAppt);
        if (firstAppt?.startTime) {
          const startDateTime = new Date(firstAppt.startTime);
          firstAppointment = {
            startDate: startDateTime.toLocaleDateString('en-GB'), // DD/MM/YYYY format
            startTime: startDateTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }), // HH:MM format
            dateTime: `${startDateTime.toLocaleDateString('en-GB')} ${startDateTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` // DD/MM/YYYY HH:MM format
          };
          console.log('[JobDetailPage] Processed firstAppointment:', firstAppointment);
        }
      } else {
        console.log('[JobDetailPage] No appointments found on job:', job?.id);
      }

      // Generate PDF with PixelVersion flag
      const pdfBytes = await PDFService.generateEditablePDF(
        initialFormData,
        form.description,
        formSections,
        {
          filename,
          headerConfigurationJson: form.headerConfigurationJson,
          headerHeight: pdfConfig.headerHeight ?? 100,
          pageMargin: pdfConfig.pageMargin ?? 15,
          headerData,
          defaultFontSize: pdfConfig.defaultFontSize,
          labelFontSize: pdfConfig.labelFontSize,
          multilineFontSize: pdfConfig.multilineFontSize,
          sectionTitleFontSize: pdfConfig.sectionTitleFontSize,
          fieldSpacing: pdfConfig.fieldSpacing,
          sectionSpacing: pdfConfig.sectionSpacing,
          checkboxListSpacing: pdfConfig.checkboxListSpacing,
          sectionPadding: pdfConfig.sectionPadding,
          fieldHeight: pdfConfig.fieldHeight,
          showSectionBorders: pdfConfig.showSectionBorders,
          checkboxListColumns: pdfConfig.checkboxListColumns,
          pixelVersion: true, // Enable PixelVersion mode
          isInteractiveView: true, // Don't pre-check checkboxes (let overlay handle state)
          job,
          customer,
          firstAppointment,
          formParameterValues,
          userDataFields: salespersonUserDataFields
        }
      );

      // Batch formResponses + pdfBytes updates together so React 18 commits
      // them in a single render cycle, preventing an intermediate flash.
      setFormResponses(prev => ({ ...prev, [form.id]: mergedFormData }));
      setPixelVersionPdfBytes(prev => ({ ...prev, [form.id]: pdfBytes }));

      // Only update loading state if not silent
      if (!silent) {
        setLoadingPixelVersionPdfs(prev => ({ ...prev, [form.id]: false }));
      }
    } catch (err: any) {
      console.error('Error generating PixelVersion PDF:', err);

      // Only show error UI if not silent (silent failures are logged only)
      if (!silent) {
        setError(extractApiErrorMessage(err, 'Failed to generate PDF'));
        setLoadingPixelVersionPdfs(prev => ({ ...prev, [form.id]: false }));
      }
    }
  };

  // Handle applying parameter changes from source forms
  const handleApplyParameterChanges = async (form: Form, updates: { [fieldCode: string]: any }) => {
    console.log('[JobDetailPage] Applying parameter changes for form:', form.id, 'updates:', updates);

    // Compute the new merged data
    const currentFormData = formResponses[form.id] || {};
    const updatedFormData = {
      ...currentFormData,
      ...updates
    };

    // Regenerate the PDF with the updated values (silent mode, using override data)
    // This will also update the formResponses state
    await generatePixelVersionPdf(form, true, updatedFormData);
  };

  // Document upload handlers


  const handleDocumentClick = async (document: JobDocument) => {
    try {
      await documentService.openDocument(document);
    } catch (err: any) {
      console.error('Failed to open document:', err);
      setError('Failed to open document');
    }
  };

  const handleDownloadDocument = async (document: JobDocument, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await documentService.saveDocument(document);
    } catch (err: any) {
      console.error('Failed to download document:', err);
      setError('Failed to download document');
    }
  };

  const handleDeleteDocument = async (document: JobDocument, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${document.originalFileName}"?`)) return;

    try {
      await documentService.deleteDocument(document.id);
      setSuccess('Document deleted successfully');

      // Immediately remove the document from the local state
      setUploadedDocuments(prev => prev.filter(d => d.id !== document.id));
    } catch (err: any) {
      console.error('Failed to delete document:', err);
      setError('Failed to delete document');
    }
  };



  // Callback when thumbnail is loaded in gallery
  const handleThumbnailLoaded = (documentId: string, url: string | null) => {
    const alreadyExists = !!sharedThumbnailUrls[documentId];
    const isSameUrl = sharedThumbnailUrls[documentId] === url;
    console.log(`[Lightbox:ThumbnailLoaded] docId: ${documentId}, hasUrl: ${!!url}, alreadyExists: ${alreadyExists}, isSameUrl: ${isSameUrl}, lightboxOpen: ${lightboxOpen}`);
    if (isSameUrl) {
      console.log(`[Lightbox:ThumbnailLoaded] SKIPPING - same URL already stored for ${documentId}`);
      return;
    }
    setSharedThumbnailUrls(prev => ({ ...prev, [documentId]: url }));
  };

  // Prepare gallery slides for lightbox
  const prepareGallerySlides = (documents: JobDocument[]) => {
    const slides = documents
      .filter(doc => {
        // Only include images and PDFs for gallery
        const ext = doc.fileExtension.toLowerCase().replace('.', '');
        return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf'].includes(ext);
      })
      .map(doc => {
        const ext = doc.fileExtension.toLowerCase().replace('.', '');
        if (ext === 'pdf') {
          return {
            src: '', // Empty src for PDFs
            alt: doc.originalFileName,
            type: 'custom',
            document: doc
          };
        } else {
          const fullImageUrl = sharedFullImageUrls[doc.id];
          const thumbnailUrl = sharedThumbnailUrls[doc.id];
          const chosenSrc = fullImageUrl || thumbnailUrl || '';
          return {
            src: chosenSrc,
            alt: doc.originalFileName,
            type: 'image',
            document: doc
          };
        }
      });
    return slides;
  };

  // Helper function to load full image data URL with deduplication
  const loadFullImageDataUrl = async (documentId: string): Promise<string | null> => {
    // Check if already loaded
    if (sharedFullImageUrls[documentId]) {
      console.log(`[Lightbox:FullImage] Already loaded for ${documentId}, returning cached (length: ${sharedFullImageUrls[documentId]?.length})`);
      return sharedFullImageUrls[documentId];
    }

    // Check if currently loading
    if (loadingImagesRef.current.has(documentId)) {
      console.log(`[Lightbox:FullImage] Skipping duplicate load request for ${documentId}`);
      return null;
    }

    // Mark as loading
    loadingImagesRef.current.add(documentId);
    console.log(`[Lightbox:FullImage] Starting download for ${documentId}, lightboxOpen: ${lightboxOpen}`);

    try {
      const blob = await documentService.downloadDocument(documentId);
      console.log(`[Lightbox:FullImage] Downloaded blob for ${documentId}, size: ${blob.size}, type: ${blob.type}`);
      const dataUrl = await new Promise<string | null>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });

      console.log(`[Lightbox:FullImage] Converted to dataUrl for ${documentId}, length: ${dataUrl?.length || 0}, lightboxOpen: ${lightboxOpen}`);

      // Store the result
      setSharedFullImageUrls(prev => {
        const alreadyStored = !!prev[documentId];
        console.log(`[Lightbox:FullImage] Storing full image URL for ${documentId}, alreadyStored: ${alreadyStored}`);
        return { ...prev, [documentId]: dataUrl };
      });

      return dataUrl;
    } catch (error) {
      console.warn('[Lightbox:FullImage] Failed to load full image for:', documentId, error);
      return null;
    } finally {
      // Remove from loading set
      loadingImagesRef.current.delete(documentId);
      console.log(`[Lightbox:FullImage] Finished loading for ${documentId}`);
    }
  };

  const handleDocumentGalleryClick = async (document: JobDocument, section: 'uploaded' | 'other') => {
    // Filter documents by section
    const sectionDocuments = uploadedDocuments.filter(d => {
      const ext = d.fileExtension.toLowerCase().replace('.', '');
      const isViewable = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf'].includes(ext);

      if (!isViewable) return false;

      // Filter by section
      const isUploaded = d.documentType === 1 || d.documentType === 'Uploaded';
      if (section === 'uploaded') {
        return isUploaded;
      } else {
        return !isUploaded;
      }
    });

    const clickedIndex = sectionDocuments.findIndex(d => d.id === document.id);

    if (clickedIndex >= 0) {
      setLightboxLoading(true);
      setLightboxSection(section);

      try {
        const clickedDocument = sectionDocuments[clickedIndex];
        const ext = clickedDocument.fileExtension.toLowerCase().replace('.', '');
        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);

        // Load thumbnail/full image if not already loaded AND it's an image (not PDF)
        if (isImage && !sharedThumbnailUrls[clickedDocument.id]) {
          if (clickedDocument.hasThumbnail) {
            // Load thumbnail for images that have one
            try {
              const thumbnailUrl = await documentService.getThumbnailDataUrl(clickedDocument.id);
              setSharedThumbnailUrls(prev => ({ ...prev, [clickedDocument.id]: thumbnailUrl }));
            } catch (error) {
              console.warn('Failed to load thumbnail for clicked image:', clickedDocument.id, error);
              setSharedThumbnailUrls(prev => ({ ...prev, [clickedDocument.id]: null }));
            }
          } else {
            // Load full image for images without thumbnails (small images)
            try {
              const blob = await documentService.downloadDocument(clickedDocument.id);
              const dataUrl = await new Promise<string | null>((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
              });
              setSharedThumbnailUrls(prev => ({ ...prev, [clickedDocument.id]: dataUrl }));
            } catch (error) {
              console.warn('Failed to load full image for clicked image:', clickedDocument.id, error);
              setSharedThumbnailUrls(prev => ({ ...prev, [clickedDocument.id]: null }));
            }
          }
        }

        // Open lightbox immediately with slides (will use already-loaded thumbnails/images from gallery)
        const slides = prepareGallerySlides(sectionDocuments);
        setLightboxSlides(slides);
        setLightboxIndex(clickedIndex);
        setLightboxOpen(true);

        // Load full resolution image for images with thumbnails (effect will update slides when loaded)
        // Skip for images without thumbnails since we already loaded the full image above
        if (isImage && clickedDocument.hasThumbnail && !sharedFullImageUrls[clickedDocument.id] && !loadingImagesRef.current.has(clickedDocument.id)) {
          loadFullImageDataUrl(clickedDocument.id);
        }

      } catch (error) {
        console.error('Failed to load lightbox for clicked image:', error);
        // Still open lightbox - they'll show fallback UI
        const slides = prepareGallerySlides(sectionDocuments);
        setLightboxSlides(slides);
        setLightboxIndex(clickedIndex);
        setLightboxOpen(true);
      } finally {
        setLightboxLoading(false);
      }
    } else {
      // Fallback to regular document opening
      handleDocumentClick(document);
    }
  };


  const handlePrintDocument = async (form: Form) => {
    if (!id || !job) return;

    try {
      setError(null);

      // Always reload form responses to ensure we have the latest data
      const [formData, formParameterValues] = await Promise.all([
        loadFormResponses(form.id),
        formService.getFormFieldParameterValues(id, form.id)
      ]);
      const initialFormData = formData || {};

      // Debug: Log to verify data is loaded
      console.log('Printing document for form:', form.description);
      console.log('Form data loaded:', initialFormData);
      console.log('Form data keys:', Object.keys(initialFormData));
      console.log('Form data has values:', Object.keys(initialFormData).length > 0);

      // Get form sections
      const formSections = form.formSections || [];

      // Generate filename
      const sanitizedFormName = form.description.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
      const sanitizedJobNumber = job.jobNumber ? job.jobNumber.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') : 'job';
      const filename = `${sanitizedJobNumber}-${sanitizedFormName}.pdf`;

      // Parse PDF configuration from form if available
      let pdfConfig: any = {};
      if (form.pdfConfigurationJson) {
        try {
          pdfConfig = JSON.parse(form.pdfConfigurationJson);
        } catch (error) {
          console.warn('Failed to parse PDF configuration JSON:', error);
        }
      }

      // Prepare header data for placeholder replacement (e.g., {{ DisplayName }})
      const headerData: { [key: string]: string | number | boolean | null | undefined } = {
        DisplayName: job.assignedSalesPersonName || ''
      };

      // Extract first appointment from job for parameter injection
      let firstAppointment = null;
      if (appointments?.length > 0) {
        const sortedAppointments = [...appointments].sort((a: any, b: any) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
        const firstAppt = sortedAppointments[0];
        console.log('[JobDetailPage] First appointment found:', firstAppt);
        if (firstAppt?.startTime) {
          const startDateTime = new Date(firstAppt.startTime);
          firstAppointment = {
            startDate: startDateTime.toLocaleDateString('en-GB'), // DD/MM/YYYY format
            startTime: startDateTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }), // HH:MM format
            dateTime: `${startDateTime.toLocaleDateString('en-GB')} ${startDateTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` // DD/MM/YYYY HH:MM format
          };
          console.log('[JobDetailPage] Processed firstAppointment:', firstAppointment);
        }
      } else {
        console.log('[JobDetailPage] No appointments found on job:', job?.id);
      }

      // Generate PDF
      const pdfBytes = await PDFService.generateEditablePDF(
        initialFormData,
        form.description,
        formSections,
        {
          filename,
          headerConfigurationJson: form.headerConfigurationJson,
          headerHeight: pdfConfig.headerHeight ?? 100,
          pageMargin: pdfConfig.pageMargin ?? 15,
          headerData,
          defaultFontSize: pdfConfig.defaultFontSize,
          labelFontSize: pdfConfig.labelFontSize,
          multilineFontSize: pdfConfig.multilineFontSize,
          sectionTitleFontSize: pdfConfig.sectionTitleFontSize,
          fieldSpacing: pdfConfig.fieldSpacing,
          sectionSpacing: pdfConfig.sectionSpacing,
          checkboxListSpacing: pdfConfig.checkboxListSpacing,
          sectionPadding: pdfConfig.sectionPadding,
          fieldHeight: pdfConfig.fieldHeight,
          showSectionBorders: pdfConfig.showSectionBorders,
          job,
          customer,
          firstAppointment,
          formParameterValues
        }
      );

      // Create blob and open in new tab
      const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Open PDF in new tab - user can print from there
      window.open(url, '_blank');

      // Clean up URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 10000);
    } catch (err: any) {
      console.error('Error printing document:', err);
      setError(extractApiErrorMessage(err, 'Failed to generate PDF for printing'));
    }
  };

  const handleAppointmentClick = (appointment: Appointment) => {
    const appointmentDate = new Date(appointment.startTime);
    navigate('/calendar', {
      state: {
        date: appointmentDate.toISOString(),
        appointmentId: appointment.id
      }
    });
  };

  // Pass lightboxSlides directly — the SlideUpdate effect already preserves
  // object references for unchanged slides, so no remapping is needed.
  const memoizedLightboxSlides = lightboxSlides;

  // Memoize all Lightbox props so it doesn't re-render on every parent render
  const lightboxClose = useCallback(() => {
    setLightboxOpen(false);
    setLightboxLoading(false);
  }, []);
  const lightboxCarousel = useMemo(() => ({ finite: false }), []);
  const lightboxAnimation = useMemo(() => ({ fade: 0, swipe: 0 }), []);
  const lightboxController = useMemo(() => ({ closeOnPullDown: true, closeOnBackdropClick: true }), []);
  const lightboxStyles = useMemo(() => ({
    root: {
      // Kill the opacity fade transition entirely — it causes iframe repaints
      opacity: 1,
      transition: 'none',
    },
    container: { backgroundColor: 'rgba(0, 0, 0, 0.9)' },
    slide: {
      width: '90vw',
      height: '100vh',
      // Isolate each slide in its own compositing layer to prevent iframe repaints
      contain: 'layout paint',
      willChange: 'transform',
    },
  } as any), []);

  // Memoize the render prop so Lightbox doesn't re-render all slides on every parent render
  const lightboxRender = useMemo(() => ({
    slide: ({ slide }: any) => {
      if (slide.type === 'custom' && slide.document) {
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
            }}
          >
            <PDFViewerIframe key={slide.document.id} document={slide.document} />
          </Box>
        );
      }

      // For images, handle cases where thumbnail failed to load
      if (slide.type === 'image' && (!slide.src || slide.src === '')) {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              backgroundColor: 'grey.100',
              p: 4
            }}
          >
            <ImageIcon sx={{ fontSize: 80, color: 'grey.500', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Failed to load image
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {slide.alt}
            </Typography>
          </Box>
        );
      }

      return undefined; // Let lightbox handle images normally
    }
  }), []);

  // Memoize the on.view handler — uses refs for values that change frequently
  const uploadedDocumentsRef = useRef(uploadedDocuments);
  uploadedDocumentsRef.current = uploadedDocuments;
  const lightboxSectionRef = useRef(lightboxSection);
  lightboxSectionRef.current = lightboxSection;
  const sharedThumbnailUrlsRef = useRef(sharedThumbnailUrls);
  sharedThumbnailUrlsRef.current = sharedThumbnailUrls;
  const sharedFullImageUrlsRef = useRef(sharedFullImageUrls);
  sharedFullImageUrlsRef.current = sharedFullImageUrls;

  const lightboxOnHandlers = useMemo(() => ({
    view: ({ index }: any) => {
      setLightboxIndex((prev: number) => prev === index ? prev : index);

      // Get section-filtered documents (same filtering as in handleDocumentGalleryClick)
      const sectionDocuments = uploadedDocumentsRef.current.filter(d => {
        const ext = d.fileExtension.toLowerCase().replace('.', '');
        const isViewable = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf'].includes(ext);

        if (!isViewable) return false;

        const isUploaded = d.documentType === 1 || d.documentType === 'Uploaded';
        if (lightboxSectionRef.current === 'uploaded') {
          return isUploaded;
        } else {
          return !isUploaded;
        }
      });

      if (index >= 0 && index < sectionDocuments.length) {
        const currentDocument = sectionDocuments[index];
        const ext = currentDocument.fileExtension.toLowerCase().replace('.', '');
        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);

        // Only load images (not PDFs)
        if (isImage) {
          // Load thumbnail/full image if not already loaded
          if (!sharedThumbnailUrlsRef.current[currentDocument.id]) {
            if (currentDocument.hasThumbnail) {
              documentService.getThumbnailDataUrl(currentDocument.id)
                .then((thumbnailUrl) => {
                  setSharedThumbnailUrls(prev => ({
                    ...prev,
                    [currentDocument.id]: thumbnailUrl
                  }));
                })
                .catch(error => {
                  console.warn('Failed to load thumbnail for navigated image:', currentDocument.id, error);
                  setSharedThumbnailUrls(prev => ({
                    ...prev,
                    [currentDocument.id]: null
                  }));
                });
            } else {
              documentService.downloadDocument(currentDocument.id)
                .then(blob => {
                  return new Promise<string | null>((resolve) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result as string);
                    reader.onerror = () => resolve(null);
                    reader.readAsDataURL(blob);
                  });
                })
                .then(dataUrl => {
                  setSharedThumbnailUrls(prev => ({
                    ...prev,
                    [currentDocument.id]: dataUrl
                  }));
                })
                .catch(error => {
                  console.warn('Failed to load full image for navigated image:', currentDocument.id, error);
                  setSharedThumbnailUrls(prev => ({
                    ...prev,
                    [currentDocument.id]: null
                  }));
                });
            }
          }

          // Load high-resolution image for images with thumbnails (if not already loaded)
          if (currentDocument.hasThumbnail && !sharedFullImageUrlsRef.current[currentDocument.id] && !loadingImagesRef.current.has(currentDocument.id)) {
            loadFullImageDataUrl(currentDocument.id);
          }
        }
      }
    }
  }), []);

  if (error && !job && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Back to Jobs
        </Button>
      </Box>
    );
  }

  if (!loading && !job) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Job not found
        </Alert>
        <Button variant="contained" onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Back to Jobs
        </Button>
      </Box>
    );
  }

  // Show loading skeleton when job is still being fetched
  if (loading && !job) {
    return (
      <Box>
        {/* Tabs skeleton */}
        <Paper sx={{ backgroundColor: theme.accentColor, mb: 3, borderRadius: 0 }}>
          <Box sx={{ display: 'flex', gap: 2, p: 1 }}>
            <Skeleton variant="rectangular" width={120} height={48} />
            <Skeleton variant="rectangular" width={110} height={48} />
            <Skeleton variant="rectangular" width={140} height={48} />
            <Skeleton variant="rectangular" width={130} height={48} />
          </Box>
        </Paper>

        {/* Content skeleton */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={4}>
            <Card sx={{ backgroundColor: theme.accentColor }}>
              <CardContent>
                <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
                <Divider sx={{ mb: 2 }} />
                {Array.from({ length: 8 }).map((_, index) => (
                  <Box key={index} display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1.5 }} />
                    <Box flex={1}>
                      <Skeleton variant="text" width={100} height={16} />
                      <Skeleton variant="text" width={200} height={24} />
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          {!embedded && (
            <Grid item xs={12} lg={8}>
              <Card sx={{ backgroundColor: theme.accentColor }}>
                <CardContent>
                  <Skeleton variant="text" width={180} height={32} sx={{ mb: 2 }} />
                  <Divider sx={{ mb: 2 }} />
                  {Array.from({ length: 5 }).map((_, index) => (
                    <Box key={index} display="flex" alignItems="center" sx={{ mb: 2 }}>
                      <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1.5 }} />
                      <Box flex={1}>
                        <Skeleton variant="text" width={100} height={16} />
                        <Skeleton variant="text" width={220} height={24} />
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  }

  // Type guard: if we've passed all checks, job should exist
  if (!job) {
    return null;
  }

  // Build the title string
  const getPageTitle = () => {
    const parts: string[] = [];
    if (job.jobAddress) {
      // Get just the first part of the address (before first comma or newline)
      const firstPart = job.jobAddress.split(',')[0].split('\n')[0].trim();
      parts.push(firstPart);
    }
    if (parts.length === 0) {
      return job.jobNumber;
    }
    return parts.join(' - ');
  };

  const pageTitle = getPageTitle();

  return (
    <>
      <Helmet>
        <title>Job: {pageTitle} | MindShare Calendar</title>
      </Helmet>

      <Box>
        {/* Header - Hidden when embedded */}
        {!embedded && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton
              onClick={handleBack}
              color="primary"
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              {loading || !job ? (
                <>
                  <Skeleton variant="text" width={250} height={40} />
                </>
              ) : (
                <Typography variant="h5" component="h5">
                  {pageTitle}
                </Typography>
              )}
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            {loading ? (
              <>
                <Skeleton variant="rectangular" width={100} height={36} />
                <Skeleton variant="rectangular" width={120} height={36} />
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleEditClick}
                  disabled={isOffline}
                >
                  Edit Job
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DuplicateIcon />}
                  onClick={handleDuplicateClick}
                  disabled={isOffline}
                >
                  Duplicate
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteJob}
                  disabled={isOffline}
                  sx={{ display: 'none' }}
                >
                  Delete
                </Button>
              </>
            )}
          </Box>
        </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ backgroundColor: theme.accentColor, mb: 3, borderRadius: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', gap: 2, p: 1 }}>
              <Skeleton variant="rectangular" width={120} height={48} />
              <Skeleton variant="rectangular" width={110} height={48} />
              <Skeleton variant="rectangular" width={140} height={48} />
              <Skeleton variant="rectangular" width={130} height={48} />
            </Box>
          ) : (
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="job detail tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="Job Overview" />
              <Tab label="Communications" />
              <Tab label="Documents" />
              {loadingForms ? (
                // Show skeleton tabs while forms are loading
                <>
                  {[1, 2, 3].map((index) => (
                    <Tab
                      key={`skeleton-${index}`}
                      disabled
                      label={
                        <Skeleton
                          variant="text"
                          width={80}
                          height={20}
                          sx={{ display: 'inline-block' }}
                        />
                      }
                    />
                  ))}
                </>
              ) : (
                forms.map((form, index) => (
                  <Tab key={form.id} label={form.description} />
                ))
              )}
            </Tabs>
          )}
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Row 1: Job Details, Customer Details, Appointments */}
            {/* Job Details - Left column */}
            <Grid item xs={12} lg={4}>
              {loading ? (
                <Card sx={{ backgroundColor: theme.accentColor }}>
                  <CardContent>
                    <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
                    <Divider sx={{ mb: 2 }} />
                    {Array.from({ length: 8 }).map((_, index) => (
                      <Box key={index} display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1.5 }} />
                        <Box flex={1}>
                          <Skeleton variant="text" width={100} height={16} />
                          <Skeleton variant="text" width={200} height={24} />
                        </Box>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              ) : (
                <JobDetailsCard job={job} theme={theme} />
              )}
            </Grid>

            {/* Customer Details - right column (only on large screens) */}
            {!embedded && (
              <Grid item xs={12} lg={8}>
                <Card sx={{ backgroundColor: theme.accentColor }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Customer Details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {loading ? (
                      <>
                        {Array.from({ length: 5 }).map((_, index) => (
                          <Box key={index} display="flex" alignItems="center" sx={{ mb: 2 }}>
                            <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1.5 }} />
                            <Box flex={1}>
                              <Skeleton variant="text" width={100} height={16} />
                              <Skeleton variant="text" width={220} height={24} />
                            </Box>
                          </Box>
                        ))}
                      </>
                    ) : customer ? (
                      <CustomerDetailsCard customer={customer} />
                    ) : (
                      <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress size={24} />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Appointments - Right column */}
            <Grid item xs={12} lg={8}>
              <AppointmentsList
                appointments={appointments}
                appointmentTypes={appointmentTypes}
                loadingAppointments={loadingAppointments}
                theme={theme}
                onAppointmentClick={handleAppointmentClick}
              />
            </Grid>


            {/* Communication directly under Appointments (8 columns) 
            <Grid item xs={12} lg={12}>
              <Card sx={{ backgroundColor: theme.accentColor }}>
                <CardContent>
                  <Typography variant="h6">Communication</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Communication logs for this job will be displayed here.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>*/}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Card sx={{ backgroundColor: theme.accentColor }}>
            <CardContent>
              <CommunicationLogForm
                jobId={id || ''}
                customerId={job?.customerId}
                onSubmit={async (data: CreateCommunicationLogDto) => {
                  await createCommunicationLogMutation.mutateAsync(data);
                  setSuccess('Communication log added successfully');
                }}
                isSubmitting={createCommunicationLogMutation.isPending}
              />
              <Typography variant="h6" gutterBottom>Communication History</Typography>
              <CommunicationLogsList
                logs={communicationLogsData || []}
                loading={communicationLogsLoading}
                emptyMessage="No communication logs found for this job."
              />
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <DocumentGallery
            jobId={id || ''}
            uploadedDocuments={uploadedDocuments}
            loadingDocuments={loadingDocuments}
            isOffline={isOffline}
            uploadConstraints={uploadConstraints}
            onDocumentClick={handleDocumentClick}
            onDocumentGalleryClick={handleDocumentGalleryClick}
            onDownloadDocument={handleDownloadDocument}
            onDeleteDocument={handleDeleteDocument}
            onReloadDocuments={loadUploadedDocuments}
            thumbnailUrls={sharedThumbnailUrls}
            onThumbnailLoaded={handleThumbnailLoaded}
            staleForms={formPdfFreshness}
            regeneratingFormIds={regeneratingFormIds}
            checkingFreshness={checkingFreshness}
          />
        </TabPanel>

        {/* Dynamic Form Tabs */}
        {forms.map((form, index) => {
          const tabIndex = 3 + index; // Start after the static tabs (Overview, Documents, Communications)
          const isPixelVersion = isPixelVersionForm(form);

          return (
            <TabPanel key={form.id} value={tabValue} index={tabIndex}>
              <Card sx={{ backgroundColor: theme.accentColor }}>
                <CardContent>
                  {loadingForms ? (
                    <Box display="flex" justifyContent="center" py={3}>
                      <CircularProgress />
                    </Box>
                  ) : isPixelVersion ? (
                    // Display PDF viewer for PixelVersion forms
                    <PixelVersionPDFViewer
                      pdfBytes={pixelVersionPdfBytes[form.id] || null}
                      loading={loadingPixelVersionPdfs[form.id] || false}
                      form={form}
                      job={job}
                      customer={customer}
                      appointments={appointments}
                      loadFormResponses={loadFormResponses}
                      generatePixelVersionPdf={generatePixelVersionPdf}
                      setSuccess={setSuccess}
                      setError={setError}
                      setSkipPdfRegeneration={setSkipPdfRegeneration}
                      userDataFields={salespersonUserDataFields}
                      initialFormData={formResponses[form.id]}
                      onDocumentSaved={() => loadUploadedDocuments(false)}
                      onApplyParameterChanges={(updates) => handleApplyParameterChanges(form, updates)}
                      onDirtyChange={setIsFormDirty}
                    />
                  ) : (
                    // Display FormRenderer for regular forms
                    <FormRenderer
                      form={form}
                      jobId={id}
                      jobNumber={job.jobNumber}
                      customerId={customer?.id}
                      assignedSalesPersonName={job.assignedSalesPersonName}
                      job={job}
                      customer={customer}
                      existingResponse={formResponses[form.id]}
                      isLoadingResponses={loadingFormResponses}
                      onSubmit={(responseData) => {
                        setSuccess('Form submitted successfully');
                        setIsFormDirty(false); // Clear dirty state after successful save
                        // Force refresh form responses after submission to get updated data
                        // This ensures other forms that depend on this form's data are updated
                        if (id) {
                          loadFormResponses(form.id, true, true);
                        }
                      }}
                      onDirtyChange={setIsFormDirty}
                    />
                  )}
                </CardContent>
              </Card>
            </TabPanel>
          );
        })}

        {/* Edit Job Dialog */}
        <BaseDialog
          open={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          title="Edit Job"
          fullWidth
          maxWidth="md"
          contentSx={{ pb: 4 }}
          actions={
            <>
              <Button
                onClick={() => setIsEditDialogOpen(false)}
                variant="outlined"
                color="primary"
              >
                Cancel
              </Button>
              <Button onClick={handleUpdateJob} variant="contained">Update</Button>
            </>
          }
        >
          {error && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <Grid container spacing={2}>
            {customer && (
              <Grid item xs={12}>
                <CustomerDetailsCard customer={customer} />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Assigned Sales Person</InputLabel>
                <Select
                  value={formData.assignedSalesPersonId || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, assignedSalesPersonId: e.target.value }))}
                  label="Assigned Sales Person"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {businessUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <JobForm
              formData={formData}
              onFormDataChange={(data) => setFormData({ ...formData, ...data })}
              defaultBounds={{
                city: business?.defaultCity,
                country: business?.defaultCountry
              }}
              showCustomerSelection={false}
            />
          </Grid>
        </BaseDialog>

        {/* Duplicate Job Dialog */}
        <DuplicateJobDialog
          open={isDuplicateDialogOpen}
          onClose={() => setIsDuplicateDialogOpen(false)}
          onDuplicate={handleDuplicateJob}
          duplicateAddress={duplicateAddress}
          onAddressChange={setDuplicateAddress}
          isDuplicating={duplicateJobMutation.isPending}
        />

        {/* Unsaved Changes Dialog */}
        <UnsavedChangesDialog
          open={showUnsavedChangesDialog}
          onStay={handleCancelLeave}
          onLeave={handleConfirmLeave}
        />

        {/* Document Gallery Lightbox — uses MemoizedLightbox to prevent parent re-renders from causing PDF iframe flashes */}
        <MemoizedLightbox
          open={lightboxOpen}
          close={lightboxClose}
          index={lightboxIndex}
          carousel={lightboxCarousel}
          animation={lightboxAnimation}
          slides={memoizedLightboxSlides}
          render={lightboxRender}
          on={lightboxOnHandlers}
          controller={lightboxController}
          styles={lightboxStyles}
        />

      </Box>
    </>
  );
};

export default JobDetailPage;
